import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { SettingsLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  DataTable,
  Badge,
  Button,
  Input,
  Select,
  Checkbox,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  ConfirmDialog,
  Alert,
  CopyToClipboard,
} from '../../../design-system/components';
import { formatDateTime } from '../../../lib/formatters';
import { admin_settings_api_keys_path, admin_settings_api_key_path } from '../../../lib/routes';

const RESOURCES = [
  { value: 'affiliates', label: 'Affiliates' },
  { value: 'campaigns', label: 'Campaigns' },
  { value: 'commissions', label: 'Commissions' },
  { value: 'payouts', label: 'Payouts' },
  { value: 'referrals', label: 'Referrals' },
  { value: 'stats', label: 'Stats' },
];

export default function ApiKeys({ api_keys, raw_key, affiliates = [] }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [affiliateId, setAffiliateId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  function resetForm() {
    setName('');
    setPermissions([]);
    setAffiliateId('');
    setErrors({});
  }

  function handleCreate() {
    setSubmitting(true);
    setErrors({});

    const payload = {
      api_key: {
        name,
        permissions,
        ...(affiliateId ? { affiliate_id: affiliateId } : {}),
      },
    };

    router.post(admin_settings_api_keys_path(), payload, {
      onSuccess: () => {
        setShowCreateModal(false);
        resetForm();
        setSubmitting(false);
      },
      onError: (errs) => {
        setErrors(errs);
        setSubmitting(false);
      },
    });
  }

  function handleRevoke(key) {
    router.delete(admin_settings_api_key_path(key.id), {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  function togglePermission(resource) {
    setPermissions((prev) =>
      prev.includes(resource)
        ? prev.filter((p) => p !== resource)
        : [...prev, resource],
    );
  }

  const allResourcesSelected = permissions.length === RESOURCES.length;

  function toggleAllPermissions() {
    if (allResourcesSelected) {
      setPermissions([]);
      return;
    }
    setPermissions(RESOURCES.map((resource) => resource.value));
  }

  const columns = [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => <span className="font-medium text-fg">{row.name}</span>,
    },
    {
      id: 'key_prefix',
      header: 'Key',
      render: (row) => (
        <span className="font-mono text-sm text-fg-secondary">{row.key_prefix}...</span>
      ),
    },
    {
      id: 'active',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.active ? 'success' : 'default'} size="sm">
          {row.active ? 'Active' : 'Revoked'}
        </Badge>
      ),
    },
    {
      id: 'permissions',
      header: 'Permissions',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {(row.permissions || []).map((p) => (
            <Badge key={p} variant="info" size="sm">
              {p}
            </Badge>
          ))}
          {row.affiliate_name && (
            <Badge variant="warning" size="sm">
              {row.affiliate_name}
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: 'last_used_at',
      header: 'Last Used',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-fg-secondary">
          {row.last_used_at ? formatDateTime(row.last_used_at) : 'Never'}
        </span>
      ),
    },
    {
      id: 'created_at',
      header: 'Created',
      sortable: true,
      render: (row) => formatDateTime(row.created_at),
    },
    {
      id: 'actions',
      header: '',
      render: (row) =>
        row.active ? (
          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(row)} className="text-danger-600 hover:text-danger-700">
            Revoke
          </Button>
        ) : null,
    },
  ];

  return (
    <SettingsLayout>
      <PageHeader
        title="API Keys"
        description="Manage read-only API keys for programmatic access"
        actions={<Button onClick={() => setShowCreateModal(true)}>Generate Key</Button>}
      />

      {raw_key && (
        <div className="mt-4">
          <Alert variant="warning" title="New API Key Created">
            <p className="mb-2">Copy this key now. It will not be shown again.</p>
            <CopyToClipboard text={raw_key}>
              <code className="block rounded bg-surface-muted px-3 py-2 font-mono text-sm break-all">
                {raw_key}
              </code>
            </CopyToClipboard>
          </Alert>
        </div>
      )}

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={api_keys}
          keyAccessor="id"
          emptyTitle="No API keys"
          emptyDescription="Generate your first API key for programmatic access."
          emptyAction={<Button onClick={() => setShowCreateModal(true)}>Generate Key</Button>}
        />
      </div>

      {/* Create Key Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        size="md"
      >
        <ModalTitle>Generate API Key</ModalTitle>
        <ModalBody>
          <div className="space-y-4">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={errors.name}
              placeholder="e.g. Production Integration"
              helperText="A descriptive name to identify this key"
            />

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className="block text-sm font-medium text-fg-secondary">
                  Resource Access (optional)
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleAllPermissions}
                  type="button"
                  className="h-auto px-0 text-sm"
                >
                  {allResourcesSelected ? 'Deselect all' : 'Select all'}
                </Button>
              </div>
              <p className="mb-2 text-sm text-fg-tertiary">
                All keys are read-only. These permissions control read access to REST resources only.
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {RESOURCES.map((resource) => (
                  <Checkbox
                    key={resource.value}
                    label={resource.label}
                    checked={permissions.includes(resource.value)}
                    onChange={() => togglePermission(resource.value)}
                  />
                ))}
              </div>
              <p className="mt-2 text-xs text-fg-tertiary">
                Integration endpoints (`/api/v1/attributions` and signed `/api/v1/events`) still work even with no resources selected.
              </p>
            </div>

            <Select
              label="Scope to Affiliate (optional)"
              value={affiliateId}
              onChange={(e) => setAffiliateId(e.target.value)}
              helperText="If set, this key will only see data for the selected affiliate."
            >
              <option value="">No affiliate scope</option>
              {affiliates.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.email})
                </option>
              ))}
            </Select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            loading={submitting}
            disabled={!name.trim()}
          >
            Generate
          </Button>
        </ModalFooter>
      </Modal>

      {/* Revoke Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleRevoke(deleteTarget)}
        title="Revoke API Key"
        message={`Are you sure you want to revoke "${deleteTarget?.name}"? Any applications using this key will no longer be able to authenticate.`}
        confirmLabel="Revoke"
        destructive
      />
    </SettingsLayout>
  );
}
